import React, { useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { ChevronLeft, ChevronRight, Mail, Calendar as CalendarIcon, Clock, UserCheck, Star } from 'lucide-react';

export default function GlobalCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    // Real-time listener for admissions_appointments
    const unsub = onSnapshot(collection(db, 'admissions_appointments'), (snap) => {
      const data = [];
      snap.forEach(doc => data.push({ id: doc.id, ...doc.data() }));
      setAppointments(data);
    });
    return () => unsub();
  }, []);

  // Calendar Math
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  const changeMonth = (offset) => {
    setCurrentDate(new Date(year, month + offset, 1));
    setSelectedDay(null);
  };

  const calendarGrid = [];
  for (let i = 0; i < firstDay; i++) {
    calendarGrid.push({ empty: true });
  }

  const today = new Date();
  for (let d = 1; d <= daysInMonth; d++) {
    const isToday = d === today.getDate() && month === today.getMonth() && year === today.getFullYear();
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dayAppointments = appointments.filter(app => app.date === dateStr);
    
    calendarGrid.push({
      empty: false,
      day: d,
      dateStr: dateStr,
      isToday: isToday,
      appointments: dayAppointments
    });
  }

  while (calendarGrid.length % 7 !== 0) {
    calendarGrid.push({ empty: true });
  }

  return (
    <div className="space-y-6 relative h-full">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-black uppercase tracking-tighter text-black">Global Calendar</h1>
          <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest mt-2">Real-time Appointment Tracker</p>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="text-2xl font-black tracking-tighter text-black uppercase">{monthNames[month]}</div>
            <div className="text-[10px] text-zinc-400 font-black uppercase tracking-[0.3em] mt-1">{year}</div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => changeMonth(-1)} className="p-3 bg-white border border-zinc-200 rounded-xl text-zinc-400 hover:text-black hover:border-black transition-all"><ChevronLeft size={16} /></button>
            <button onClick={() => changeMonth(1)} className="p-3 bg-white border border-zinc-200 rounded-xl text-zinc-400 hover:text-black hover:border-black transition-all"><ChevronRight size={16} /></button>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-7 gap-px bg-zinc-200 border border-zinc-200 rounded-2xl overflow-hidden shadow-sm">
        {/* Day Names */}
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="bg-zinc-50 p-4 text-center text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500 border-b border-zinc-200">
            {day}
          </div>
        ))}

        {/* Day Cells */}
        {calendarGrid.map((cell, idx) => (
          <div 
            key={idx}
            onClick={() => !cell.empty && setSelectedDay(cell)}
            className={`min-h-[120px] p-4 bg-white relative group transition-all duration-200 ${cell.empty ? 'bg-zinc-50 opacity-50' : 'cursor-pointer hover:bg-zinc-50 active:scale-[0.98]'} ${selectedDay?.dateStr === cell.dateStr ? 'ring-2 ring-inset ring-black' : ''}`}
          >
            {!cell.empty && (
              <>
                <div className={`text-lg font-black mb-2 flex items-center justify-between ${cell.isToday ? 'text-[#8B1A2D]' : 'text-zinc-300 group-hover:text-black transition-colors'}`}>
                  <span>{cell.day}</span>
                  {cell.appointments.length > 0 && (
                    <span className="text-[10px] bg-zinc-100 text-zinc-600 px-2 py-1 rounded-md">{cell.appointments.length} Apps</span>
                  )}
                </div>
                
                <div className="space-y-1">
                  {cell.appointments.slice(0, 3).map((app, eIdx) => (
                    <div key={eIdx} className={`text-[9px] font-bold uppercase tracking-wider p-1.5 rounded truncate ${app.type === 'Workspace Support' ? (app.status === 'assigned' ? 'bg-blue-200 text-blue-900' : 'bg-blue-100 text-blue-800') : (app.status === 'assigned' ? 'bg-[#8B1A2D]/10 text-[#8B1A2D]' : 'bg-yellow-100 text-yellow-800')}`}>
                      {app.timeSlot} - {app.studentName}
                    </div>
                  ))}
                  {cell.appointments.length > 3 && (
                    <div className="text-[9px] font-bold text-zinc-400 p-1 text-center">+{cell.appointments.length - 3} more</div>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Selected Day Slide-up Panel */}
      {selectedDay && (
        <div className="fixed bottom-0 left-64 right-0 bg-white border-t border-zinc-200 p-6 shadow-[0_-10px_40px_rgba(0,0,0,0.05)] z-20 flex flex-col max-h-[50vh]">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-black uppercase tracking-tighter text-black flex items-center gap-2">
              <CalendarIcon size={20} className="text-[#8B1A2D]"/> Schedule for {monthNames[month]} {selectedDay.day}, {year}
            </h2>
            <button onClick={() => setSelectedDay(null)} className="text-[10px] font-black tracking-widest uppercase text-zinc-400 hover:text-black">Close Panel</button>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {selectedDay.appointments.length === 0 ? (
              <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest text-center py-8">No appointments booked for this day.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {selectedDay.appointments.map(app => (
                  <div key={app.id} className="border border-zinc-200 rounded-xl p-4 flex flex-col">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock size={14} className="text-zinc-400"/>
                      <span className="text-sm font-black text-black">{app.timeSlot}</span>
                      {app.type === 'Workspace Support' && (
                        <span className="ml-auto text-[9px] font-bold uppercase tracking-widest bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">Workspace Support</span>
                      )}
                      {app.type === 'Installment Setup Interview' && (
                        <span className="ml-auto text-[9px] font-bold uppercase tracking-widest bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">Installments</span>
                      )}
                    </div>
                    <div className="text-xs font-bold text-zinc-600 mb-1">{app.studentName}</div>
                    <div className="text-[10px] text-zinc-400 mb-4">{app.studentEmail}</div>
                    
                    <div className={`mt-auto pt-4 border-t border-zinc-100 text-[10px] font-black uppercase tracking-widest flex items-center gap-1 ${app.status === 'assigned' ? 'text-green-600' : 'text-yellow-600'}`}>
                      {app.status === 'assigned' ? (
                        <><UserCheck size={12}/> Assigned to: {app.advisorEmail}</>
                      ) : (
                        <><Star size={12}/> Unassigned (Pending Dispatch)</>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
